/**
 * Notification Redux Reducer
 * Manages notification state updates
 */

import * as types from './types';

const initialState: types.NotificationState = {
  notifications: [],
  unreadCount: 0,
  preferences: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  },
  lastFetched: null,
};

const notificationReducer = (
  state = initialState,
  action: types.NotificationActionTypes
): types.NotificationState => {
  switch (action.type) {
    // Fetch Notifications
    case types.FETCH_NOTIFICATIONS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case types.FETCH_NOTIFICATIONS_SUCCESS:
      return {
        ...state,
        loading: false,
        notifications: action.payload.notifications,
        pagination: action.payload.pagination,
        lastFetched: Date.now(),
        error: null,
      };

    case types.FETCH_NOTIFICATIONS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Fetch Unread Count
    case types.FETCH_UNREAD_COUNT_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case types.FETCH_UNREAD_COUNT_SUCCESS:
      return {
        ...state,
        loading: false,
        unreadCount: action.payload,
        error: null,
      };

    case types.FETCH_UNREAD_COUNT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Mark as Read
    case types.MARK_AS_READ_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case types.MARK_AS_READ_SUCCESS:
      return {
        ...state,
        loading: false,
        notifications: state.notifications.map((notification) =>
          notification._id === action.payload
            ? { ...notification, isRead: true, status: 'read' as const, readAt: new Date().toISOString() }
            : notification
        ),
        error: null,
      };

    case types.MARK_AS_READ_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Mark All as Read
    case types.MARK_ALL_AS_READ_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case types.MARK_ALL_AS_READ_SUCCESS:
      return {
        ...state,
        loading: false,
        notifications: state.notifications.map((notification) => ({
          ...notification,
          isRead: true,
          status: 'read' as const,
          readAt: new Date().toISOString(),
        })),
        error: null,
      };

    case types.MARK_ALL_AS_READ_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Delete Notification
    case types.DELETE_NOTIFICATION_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case types.DELETE_NOTIFICATION_SUCCESS:
      return {
        ...state,
        loading: false,
        notifications: state.notifications.filter(
          (notification) => notification._id !== action.payload
        ),
        error: null,
      };

    case types.DELETE_NOTIFICATION_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Fetch Preferences
    case types.FETCH_PREFERENCES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case types.FETCH_PREFERENCES_SUCCESS:
      return {
        ...state,
        loading: false,
        preferences: action.payload,
        error: null,
      };

    case types.FETCH_PREFERENCES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Update Preferences
    case types.UPDATE_PREFERENCES_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case types.UPDATE_PREFERENCES_SUCCESS:
      return {
        ...state,
        loading: false,
        preferences: action.payload,
        error: null,
      };

    case types.UPDATE_PREFERENCES_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    // Add Notification (real-time)
    case types.ADD_NOTIFICATION:
      // Prepend new notification to the list
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        pagination: {
          ...state.pagination,
          total: state.pagination.total + 1,
        },
      };

    // Remove Notification
    case types.REMOVE_NOTIFICATION:
      return {
        ...state,
        notifications: state.notifications.filter(
          (notification) => notification._id !== action.payload
        ),
      };

    // Clear Notifications
    case types.CLEAR_NOTIFICATIONS:
      return {
        ...state,
        notifications: [],
        unreadCount: 0,
        pagination: initialState.pagination,
      };

    // Increment Unread Count
    case types.INCREMENT_UNREAD_COUNT:
      return {
        ...state,
        unreadCount: state.unreadCount + 1,
      };

    // Decrement Unread Count
    case types.DECREMENT_UNREAD_COUNT:
      return {
        ...state,
        unreadCount: Math.max(0, state.unreadCount - 1),
      };

    default:
      return state;
  }
};

export default notificationReducer;
